import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from '@/store/useAuthStore';
import { changeStatusAsking } from '@/lib/statusChangeAsk';
import { BASE_PAGES_PER_ALBUM } from '@/lib/orderPricing';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ordersApi, uploadLinkApi, settingsApi, coversApi, uploadToSignedUrl, type Order } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { pdfPageCount, pdfPageThumbnail } from '@/lib/pdfRaster';
import { importNativeAlbum, type CoverCase } from '@/lib/importNativeAlbum';
import { selectReadyOrders, type SendToPrinterResult } from '@/lib/sendToPrinter';
import { albumCountForBundle } from '@/lib/followUpStateMachine';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { AdminLayout } from '@/components/AdminLayout';
import { useAmeexConfirmPrint, AmeexConfirmPrintButton, AmeexBatchResult } from '@/components/admin/AmeexConfirmPrint';
import { formatDateTime, formatPrice, formatPhone } from '@/lib/utils';
import { SourceBadge } from '@/components/SourceBadge';
import { OrderItemThumbs } from '@/components/OrderItemThumbs';
import { orderActionState } from '@/lib/orderActionState';
import { DeviceBadge } from '@/components/DeviceBadge';
import { OriginSelect } from '@/components/admin/OriginSelect';
import { useT } from '@/lib/i18n';
import { paidState } from '@/lib/paidState';
import { useIsPhone } from '@/lib/phoneViewport';
import { PhoneActionBar, SelectTick } from '@/components/admin/PhoneActionBar';

const STATUSES = [
  { value: '', label: 'Tous les statuts' },
  { value: 'pending-album', label: 'En attente' },
  { value: 'album-submitted', label: 'Album soumis' },
  { value: 'paid', label: 'Payée' },
  { value: 'ready-to-print', label: 'Prêt à imprimer' },
  { value: 'printed', label: 'Imprimé' },
  { value: 'in-production', label: 'En production' },
  { value: 'ready-to-deliver', label: 'Prêt à livrer' },
  { value: 'shipped', label: 'Expédié' },
  { value: 'delivered', label: 'Livré' },
  { value: 'cancelled', label: 'Annulé' },
  { value: 'in-redo', label: 'En redo' },
];
/** The list filter only — never a status anyone can SET (migration 226: an
 *  archived order is one made before the photos-or-money rule with neither). */
const FILTERS = [...STATUSES, { value: 'archived', label: 'Archivées (ni photos ni argent)' }];

/** An archived order is not « Annulé »: she never cancelled, it should not have existed yet. */
function OrderStatusBadge({ order }: { order: { status: string; archived_at?: string | null } }) {
  const { t } = useT();
  if (order.archived_at) {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border bg-gray-50 text-gray-500 border-gray-200">
        {t('Archivée')}
      </span>
    );
  }
  return <StatusBadge status={order.status} />;
}

export default function AdminOrdersPage() {
  const { t } = useT();
  // Sync the status filter with ?status=... so the dashboard can deep-link
  // into a filtered view (e.g. clicking "Albums à valider" → ?status=album-submitted).
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') ?? '';
  const initialSearch = searchParams.get('q') ?? '';

  const [orders, setOrders] = useState<Order[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(initialSearch);
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [page, setPage] = useState(1);

  // Keep URL in sync when the user changes the filter from the dropdown,
  // so refreshing or sharing the URL preserves the view.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (statusFilter) next.set('status', statusFilter); else next.delete('status');
    if (search)       next.set('q', search);            else next.delete('q');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, search]);
  const [showManualModal, setShowManualModal] = useState(false);
  const [showUploadLinkModal, setShowUploadLinkModal] = useState(false);
  // Multi-select for bulk delete. Keyed by shopify_order_id since that's
  // the public id the bulk endpoint accepts. Cleared on page change.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Cards below md, table above — the shared admin phone switch.
  const isCompact = useIsPhone();
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [showBulkStatus, setShowBulkStatus] = useState(false);
  const [showSendToPrinter, setShowSendToPrinter] = useState(false);
  // Confirmer + imprimer — same AMEEX batch flow as the Livraison AMEEX page.
  // Clears the selection on success; the realtime `orders` subscription then
  // refreshes the list, and the hook invalidates the AMEEX dashboard cache.
  const confirmPrint = useAmeexConfirmPrint(() => setSelected(new Set()));

  const allSelected = orders.length > 0 && orders.every(o => selected.has(o.shopify_order_id));
  const someSelected = selected.size > 0 && !allSelected;
  function toggleOne(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }
  function toggleAll() {
    setSelected(prev => {
      if (orders.every(o => prev.has(o.shopify_order_id))) {
        const next = new Set(prev);
        orders.forEach(o => next.delete(o.shopify_order_id));
        return next;
      }
      const next = new Set(prev);
      orders.forEach(o => next.add(o.shopify_order_id));
      return next;
    });
  }
  // Nicety: select every visible "En production" order in one tap (the typical
  // send-to-printer batch). Replaces the current selection.
  function selectInProduction() {
    setSelected(new Set(orders.filter(o => o.status === 'in-production').map(o => o.shopify_order_id)));
  }

  useEffect(() => {
    document.title = 'Admin — Storiz';
  }, []);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string> = { page: String(page), limit: '30' };
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const data = await ordersApi.list(params);
      setOrders(data.orders);
      setTotal(data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Drop the selection whenever the visible page or filter changes — keeping
  // off-page selections invisibly checked is a foot-gun for bulk delete.
  useEffect(() => { setSelected(new Set()); }, [page, statusFilter, search]);

  // Realtime subscription
  useEffect(() => {
    const channel = supabase
      .channel('admin-orders')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'orders' },
        () => fetchOrders()
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [fetchOrders]);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
    fetchOrders();
  }

  return (
    <AdminLayout
      title={t('Commandes')}
      subtitle={t('{n} commande(s) au total', { n: total })}
      actions={
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="secondary" onClick={() => setShowUploadLinkModal(true)}>
            {t('+ Lien d\'upload client')}
          </Button>
          <Button size="sm" onClick={() => setShowManualModal(true)}>
            {t('+ Nouvelle commande')}
          </Button>
        </div>
      }
    >
      <>
        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('Rechercher : nom, téléphone, commande, destination (ex. New York)…')}
                className="flex-1 min-w-0 px-3.5 py-2 min-h-11 md:min-h-0 border border-gray-200 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <Button type="submit" size="sm">{t('Rechercher')}</Button>
            </form>

            {/* Status filter */}
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="px-3.5 py-2 min-h-11 md:min-h-0 border border-gray-200 rounded-lg text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white"
            >
              {FILTERS.map((s) => (
                <option key={s.value} value={s.value}>{t(s.label)}</option>
              ))}
              {/* A home Pipeline step links here with several statuses
                  (« paid,ready-to-print »): show it as one option. */}
              {statusFilter.includes(',') && (
                <option value={statusFilter}>
                  {statusFilter.split(',').map((v) => t(STATUSES.find((x) => x.value === v)?.label ?? v)).join(' + ')}
                </option>
              )}
            </select>
          </div>
        </div>

        {showManualModal && (
          <ManualOrderModal
            onClose={() => setShowManualModal(false)}
            onCreated={() => { setShowManualModal(false); fetchOrders(); }}
            onRefresh={() => fetchOrders()}
          />
        )}

        {showUploadLinkModal && (
          <ManualOrderModal
            mode="upload-link"
            onClose={() => setShowUploadLinkModal(false)}
            onCreated={() => { setShowUploadLinkModal(false); fetchOrders(); }}
          />
        )}

        {/* Quick-select helper — always available so the admin can grab the
            whole "En production" batch for the printer in one tap. */}
        {orders.some(o => o.status === 'in-production') && (
          <div className="mb-3 flex justify-end">
            <Button size="sm" variant="ghost" onClick={selectInProduction}>
              {t('Sélectionner « En production »')}
            </Button>
          </div>
        )}

        {/* Bulk action bar — only shows when something is selected. */}
        {/* flex-wrap, not a single row: five buttons never fit a phone, and
            without wrapping they widened the whole page and dragged the table
            into a sideways scroll. */}
        {selected.size > 0 && !isCompact && (
          <div data-inline-bulk-bar className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5">
            <div className="text-sm text-gray-900">
              {t('{n} commande(s) sélectionnée(s)', { n: selected.size })}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                {t('Effacer la sélection')}
              </Button>
              <AmeexConfirmPrintButton
                ids={Array.from(selected)}
                run={confirmPrint.run}
                isPending={confirmPrint.isPending}
              />
              <Button size="sm" variant="primary" onClick={() => setShowSendToPrinter(true)}>
                {t('Envoyer à l\'imprimeur')}
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setShowBulkStatus(true)}>
                {t('Changer le statut')}
              </Button>
              <Button size="sm" variant="danger" onClick={() => setShowBulkDelete(true)}>
                {selected.size > 1 ? t('Supprimer {n} commandes', { n: selected.size }) : t('Supprimer la commande')}
              </Button>
            </div>
          </div>
        )}
        {/* Result of the last confirm+print batch — kept visible after the
            selection clears so the reprint / bordereau fallbacks stay handy. */}
        <div className="mb-3"><AmeexBatchResult
          summary={confirmPrint.summary}
          lastBatch={confirmPrint.lastBatch}
          results={confirmPrint.results}
          orders={orders}
          onRevalidate={() => confirmPrint.run(confirmPrint.lastIds)}
          isPending={confirmPrint.isPending}
        /></div>

        {/* Orders Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="space-y-4 p-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-16 bg-gray-50 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-16">
              <div className="text-4xl mb-3">📭</div>
              <p className="text-gray-500">{t('Aucune commande trouvée')}</p>
            </div>
          ) : isCompact ? (
            /* ── MOBILE ─────────────────────────────────────────────────────
               The table hid Formule, Destination, Prix and Date below md, so a
               phone showed only number/customer/status and every order had to
               be opened to learn anything. Cards carry the same fields the
               desktop columns do, stacked, so the list can be triaged in one
               pass. Rendered INSTEAD of the table (not merely CSS-hidden) so
               each row's status menu and confirm dialog exist exactly once. */
            <ul className="divide-y divide-gray-100">
              {/* The table header's « select all », for cards: same toggleAll. */}
              <li className="flex items-center gap-1 pl-0.5 pr-3 py-0.5 bg-gray-50/60">
                <SelectTick
                  checked={allSelected}
                  onChange={toggleAll}
                  label={t('Sélectionner toutes les commandes visibles')}
                />
                <span className="text-[13px] text-gray-500">{t('Tout sélectionner ({n})', { n: orders.length })}</span>
              </li>
              {orders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  selected={selected.has(order.shopify_order_id)}
                  onToggle={() => toggleOne(order.shopify_order_id)}
                  onStatusChanged={(newStatus) =>
                    setOrders((prev) =>
                      prev.map((o) =>
                        o.shopify_order_id === order.shopify_order_id ? { ...o, status: newStatus } : o,
                      ),
                    )
                  }
                />
              ))}
            </ul>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <th className="px-3 py-3 w-10">
                      <input
                        type="checkbox"
                        aria-label={t('Sélectionner toutes les commandes visibles')}
                        checked={allSelected}
                        ref={(el) => { if (el) el.indeterminate = someSelected; }}
                        onChange={toggleAll}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500">{t('Commande')}</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500">{t('Client')}</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 hidden md:table-cell">{t('Formule')}</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 hidden lg:table-cell">{t('Destination')}</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 hidden lg:table-cell">{t('Prix')}</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500">{t('Statut')}</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 hidden md:table-cell">{t('Date')}</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {orders.map((order) => (
                    <OrderRow
                      key={order.id}
                      order={order}
                      selected={selected.has(order.shopify_order_id)}
                      onToggle={() => toggleOne(order.shopify_order_id)}
                      onStatusChanged={(newStatus) =>
                        setOrders((prev) =>
                          prev.map((o) =>
                            o.shopify_order_id === order.shopify_order_id ? { ...o, status: newStatus } : o,
                          ),
                        )
                      }
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Phone: the bulk bar is pinned above the bottom tab bar, so it is
            still there after scrolling down the list to tick. Same four
            actions, same handlers as the inline desktop bar above. */}
        {selected.size > 0 && (
          <PhoneActionBar
            label={t('{n} commande(s) sélectionnée(s)', { n: selected.size })}
            onClear={() => setSelected(new Set())}
            clearLabel={t('Effacer')}
          >
            <AmeexConfirmPrintButton
              ids={Array.from(selected)}
              run={confirmPrint.run}
              isPending={confirmPrint.isPending}
              className="min-h-11 px-3 bg-amber-500 active:bg-amber-600 text-white text-sm font-semibold rounded-lg disabled:opacity-50"
            />
            <Button variant="primary" onClick={() => setShowSendToPrinter(true)}>
              {t('Envoyer à l\'imprimeur')}
            </Button>
            <Button variant="secondary" onClick={() => setShowBulkStatus(true)}>
              {t('Changer le statut')}
            </Button>
            <Button variant="danger" onClick={() => setShowBulkDelete(true)}>
              {selected.size > 1 ? t('Supprimer {n} commandes', { n: selected.size }) : t('Supprimer la commande')}
            </Button>
          </PhoneActionBar>
        )}

        {showBulkDelete && (
          <BulkDeleteModal
            orders={orders.filter(o => selected.has(o.shopify_order_id))}
            onClose={() => setShowBulkDelete(false)}
            onDone={() => {
              setShowBulkDelete(false);
              setSelected(new Set());
              fetchOrders();
            }}
          />
        )}

        {showBulkStatus && (
          <BulkStatusModal
            orders={orders.filter(o => selected.has(o.shopify_order_id))}
            onClose={() => setShowBulkStatus(false)}
            onDone={() => {
              setShowBulkStatus(false);
              setSelected(new Set());
              fetchOrders();
            }}
          />
        )}

        {showSendToPrinter && (
          <SendToPrinterModal
            orders={orders.filter(o => selected.has(o.shopify_order_id))}
            onClose={() => setShowSendToPrinter(false)}
          />
        )}

        {/* Pagination */}
        {total > 30 && !loading && (
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-gray-500">
              {t('Page {page} · {pages} pages', { page, pages: Math.ceil(total / 30) })}
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                {t('Précédent')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= Math.ceil(total / 30)}
                onClick={() => setPage((p) => p + 1)}
              >
                {t('Suivant')}
              </Button>
            </div>
          </div>
        )}
      </>
    </AdminLayout>
  );
}

// ─── Manual Order Modal ──────────────────────────────────────────────────────

// ── Client-PDF import: pdfjs thumbnail helper (mirrors previewAssets.ts) ──────
// Lazy-loads pdfjs only when an admin actually imports a PDF, so it stays out
// of the normal admin bundle. Renders a single page to a small data-URL.
// Per-album import state. Cover and interior can each be provided alone or
// together; the cover handles 3 input shapes (A full-wrap / B front+back /
// C front A4 alone). See importNativeAlbum for how these become a native album.
interface AlbumImportState {
  coverBytes: Uint8Array | null;
  coverFileName: string;
  coverPages: number;
  coverCase: CoverCase;
  frontIdx: number;       // B / C
  backIdx: number;        // B (-1 = none)
  wrapBackRight: number;  // A — back region ends (fraction of wrap width)
  wrapFrontLeft: number;  // A — front region begins
  thumbs: { front?: string; back?: string };
  interiorBytes: Uint8Array | null;
  interiorFileName: string;
  interiorPages: number;
  /** Server verdict per file. Checked at PICK time so a wrong file is caught
   *  before the order is created and before the minutes-long rasterize loop —
   *  not silently reshaped on its way to print. */
  coverCheck: SpecCheck;
  interiorCheck: SpecCheck;
}

/** null = nothing picked yet. */
type SpecCheck =
  | null
  | { state: 'checking' }
  | { state: 'ok'; detail: string }
  | { state: 'bad'; message: string; detail?: string };

function emptyAlbumImport(): AlbumImportState {
  return {
    coverBytes: null, coverFileName: '', coverPages: 0, coverCase: 'B',
    frontIdx: 0, backIdx: 1, wrapBackRight: 0.485, wrapFrontLeft: 0.515,
    thumbs: {},
    interiorBytes: null, interiorFileName: '', interiorPages: 0,
    coverCheck: null, interiorCheck: null,
  };
}

/** Inline verdict under a file input. Deliberately sits NEXT TO the field it
 *  judges rather than in a banner at the top of the modal, so a Trio operator
 *  can tell which of six files is the problem. */
function SpecVerdict({ check }: { check: SpecCheck }) {
  if (!check) return null;
  if (check.state === 'checking') {
    return (
      <p className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-500">
        <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-gray-500" />
        Vérification du format…
      </p>
    );
  }
  if (check.state === 'ok') {
    return (
      <p className="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-emerald-700">
        <span aria-hidden>✓</span>{check.detail}
      </p>
    );
  }
  return (
    <div className="mt-1.5 rounded-lg border border-red-200 bg-red-50 px-2.5 py-2">
      <p className="text-xs font-semibold text-red-800">Format refusé — fichier non importé</p>
      <p className="mt-0.5 text-[11px] leading-relaxed text-red-700 whitespace-pre-wrap">{check.message}</p>
    </div>
  );
}

/** Upload the picked PDF to a staging path and ask the worker to measure it.
 *  The worker owns the rules (it reuses the same validators as the manual
 *  cover-replace flow), so they exist in exactly one place. */
async function runSpecCheck(bytes: Uint8Array, kind: 'cover' | 'interior'): Promise<SpecCheck> {
  try {
    const signed = await ordersApi.importValidateUploadUrl(kind);
    await uploadToSignedUrl(
      signed.upload_url,
      new File([bytes as BlobPart], `${kind}.pdf`, { type: 'application/pdf' }),
      'application/pdf',
    );
    const v = await ordersApi.importValidate(signed.storage_path, kind);
    if (!v.ok) return { state: 'bad', message: v.message ?? 'Format refusé.' };
    if (kind === 'cover') {
      return { state: 'ok', detail: `${v.actual?.wmm} × ${v.actual?.hmm} mm` };
    }
    const shape = v.expected?.trim ? `${v.expected.trim.wmm} × ${v.expected.trim.hmm} mm` : '';
    return {
      state: 'ok',
      detail: `${v.pages} page${v.pages > 1 ? 's' : ''} · ${v.distinct_sizes?.[0] ?? shape}`
        + (v.rotation_normalised ? ' · rotation corrigée' : ''),
    };
  } catch (e) {
    const body = (e as { body?: { message?: string } })?.body;
    return { state: 'bad', message: body?.message ?? (e as Error)?.message ?? 'Vérification impossible.' };
  }
}

function ManualOrderModal({
  onClose,
  onCreated,
  onRefresh,
  mode = 'order',
}: {
  onClose: () => void;
  onCreated: () => void;
  /** Refresh the orders list WITHOUT closing the modal — used after an import
   *  so the admin sees the "Import terminé ✓" success state and closes it via
   *  the Fermer button themselves. */
  onRefresh?: () => void;
  mode?: 'order' | 'upload-link';
}) {
  const { t } = useT();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    bundle: 'Solo' as 'Solo' | 'Duo' | 'Trio',
    page_count: BASE_PAGES_PER_ALBUM,
    total_price: '',
    customer_note: '',
    layout: '',
    origin: null as string | null,
    destination_1: '', year_1: '',
    destination_2: '', year_2: '',
    destination_3: '', year_3: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // For upload-link mode: holds the generated link details after creation.
  const [generatedLink, setGeneratedLink] = useState<{
    upload_url: string;
    expires_at: string;
    customer_phone: string;
    customer_name: string;
  } | null>(null);
  const [copied, setCopied] = useState(false);

  const albumCount = form.bundle === 'Solo' ? 1 : form.bundle === 'Duo' ? 2 : 3;

  // ── Client-PDF import state (manual order only) ──────────────────────────────
  const [importEnabled, setImportEnabled] = useState(false);
  const [imports, setImports] = useState<AlbumImportState[]>([emptyAlbumImport()]);
  const [importErr, setImportErr] = useState<string | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importProgress, setImportProgress] = useState<string | null>(null);
  const [importDone, setImportDone] = useState(false);
  // The order created by the import, so the "done" button can open it.
  const [createdOrderId, setCreatedOrderId] = useState<string | null>(null);

  // Keep one import slot per album in the bundle (Solo=1, Duo=2, Trio=3).
  useEffect(() => {
    setImports((prev) => {
      if (prev.length === albumCount) return prev;
      const next = prev.slice(0, albumCount);
      while (next.length < albumCount) next.push(emptyAlbumImport());
      return next;
    });
  }, [albumCount]);

  const patchImport = useCallback((i: number, patch: Partial<AlbumImportState>) => {
    setImports((prev) => prev.map((im, k) => (k === i ? { ...im, ...patch } : im)));
  }, []);

  // Render the front (+ back) cover thumbnail(s) for one album's confirm panel.
  const refreshCoverThumbs = useCallback(async (i: number, im: AlbumImportState) => {
    if (!im.coverBytes) { patchImport(i, { thumbs: {} }); return; }
    try {
      const thumbs: { front?: string; back?: string } = {};
      if (im.coverCase === 'A') {
        thumbs.front = await pdfPageThumbnail(im.coverBytes, Math.max(0, im.frontIdx));
      } else {
        thumbs.front = await pdfPageThumbnail(im.coverBytes, im.frontIdx);
        if (im.coverCase === 'B' && im.backIdx >= 0) {
          thumbs.back = await pdfPageThumbnail(im.coverBytes, im.backIdx);
        }
      }
      patchImport(i, { thumbs });
    } catch { /* thumb is best-effort */ }
  }, [patchImport]);

  // A refused file can never be imported, and a check still running blocks the
  // button rather than racing it. Reject-only policy: there is no "import
  // anyway" — a bad file gets re-exported, not reshaped.
  const importBlocked = imports.some(
    (im) => im.coverCheck?.state === 'bad' || im.interiorCheck?.state === 'bad'
      || im.coverCheck?.state === 'checking' || im.interiorCheck?.state === 'checking',
  );

  async function onCoverFileSelect(i: number, file: File | null) {
    setImportErr(null);
    if (!file) { patchImport(i, { coverBytes: null, coverFileName: '', coverPages: 0, thumbs: {}, coverCheck: null }); return; }
    try {
      const buf = new Uint8Array(await file.arrayBuffer());
      const pages = await pdfPageCount(buf);
      // 1 page → most likely a full wrap (A) or a front-alone (C); default A so
      // the admin re-fits it. 2+ pages → front+back (B).
      const kase: CoverCase = pages <= 1 ? 'A' : 'B';
      const next: AlbumImportState = {
        ...imports[i], coverBytes: buf, coverFileName: file.name, coverPages: pages,
        coverCase: kase, frontIdx: 0, backIdx: pages >= 2 ? 1 : -1, thumbs: {},
        coverCheck: { state: 'checking' },
      };
      patchImport(i, next);
      refreshCoverThumbs(i, next);
      // Only a single-page full wrap is checked against the vinyl sheet spec —
      // a B/C cover is separate A4 panels we composite ourselves, so the sheet
      // size does not apply to the uploaded file.
      if (kase === 'A' && pages === 1) {
        patchImport(i, { coverCheck: await runSpecCheck(buf, 'cover') });
      } else {
        patchImport(i, { coverCheck: { state: 'ok', detail: t('{n} page(s) — recomposé dans nos specs', { n: pages }) } });
      }
    } catch (e: any) {
      setImportErr(e?.message ?? t('PDF illisible'));
      patchImport(i, { coverBytes: null, coverFileName: '', coverPages: 0, coverCheck: null });
    }
  }

  async function onInteriorFileSelect(i: number, file: File | null) {
    setImportErr(null);
    if (!file) { patchImport(i, { interiorBytes: null, interiorFileName: '', interiorPages: 0, interiorCheck: null }); return; }
    try {
      const buf = new Uint8Array(await file.arrayBuffer());
      const pages = await pdfPageCount(buf);
      patchImport(i, { interiorBytes: buf, interiorFileName: file.name, interiorPages: pages, interiorCheck: { state: 'checking' } });
      patchImport(i, { interiorCheck: await runSpecCheck(buf, 'interior') });
    } catch (e: any) {
      setImportErr(e?.message ?? t('PDF illisible'));
      patchImport(i, { interiorBytes: null, interiorFileName: '', interiorPages: 0, interiorCheck: null });
    }
  }

  function update<K extends keyof typeof form>(k: K, v: typeof form[K]) {
    setForm((p) => ({ ...p, [k]: v }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.customer_name.trim() || !form.customer_phone.trim()) {
      setError(t('Nom et téléphone sont obligatoires'));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        customer_email: form.customer_email || undefined,
        bundle: form.bundle,
        page_count: Number(form.page_count) || 30,
        total_price: form.total_price ? Number(form.total_price) : undefined,
        customer_note: form.customer_note || undefined,
        layout: form.layout || undefined,
        origin: form.origin || undefined,
        destination_1: form.destination_1 || undefined,
        year_1: form.year_1 || undefined,
        destination_2: albumCount >= 2 ? (form.destination_2 || undefined) : undefined,
        year_2:        albumCount >= 2 ? (form.year_2 || undefined)        : undefined,
        destination_3: albumCount >= 3 ? (form.destination_3 || undefined) : undefined,
        year_3:        albumCount >= 3 ? (form.year_3 || undefined)        : undefined,
      };
      const { order } = await ordersApi.createManual(payload);
      setCreatedOrderId(order.shopify_order_id);

      if (mode === 'upload-link') {
        // Generate the magic link, then show it in a success view —
        // do NOT auto-navigate. Admin copies the link and shares with client.
        try {
          const link = await uploadLinkApi.generate(order.shopify_order_id);
          setGeneratedLink({
            upload_url: link.upload_url,
            expires_at: link.expires_at,
            customer_phone: payload.customer_phone,
            customer_name: payload.customer_name,
          });
          // Refresh the orders list in the background so the new row appears
          // when the modal closes, but stay on the success view.
          onCreated();
        } catch (linkErr: any) {
          // Order was created but link gen failed — surface the error so admin
          // can retry from the order detail page rather than leaving them stuck.
          setError(t('Commande créée mais génération du lien échouée : {error}. Ouvrez la commande pour réessayer.', { error: linkErr?.message ?? t('erreur inconnue') }));
          setTimeout(() => navigate(`/admin/orders/${order.shopify_order_id}`), 2500);
        }
      } else if (importEnabled && imports.some((im) => im.coverBytes || im.interiorBytes)) {
        // ── Client-PDF import → NATIVE editable albums ──────────────────────────
        // The order row now exists. For each album that has a cover and/or an
        // interior, rasterize the client PDFs into our native model (photos +
        // 1-up pages + blank-cover image) and render through the normal pipeline
        // (our geometry, branding, intro/outro, 3D preview). Stays in the modal
        // to show progress + a success state.
        try {
          setImportBusy(true);
          setImportProgress(t('Préparation...'));
          const covers = (await coversApi.list()).covers;
          const { settings } = await settingsApi.get();
          const results: Array<{ albumIndex: number; rendered: boolean; pageCount: number }> = [];
          const dest = [form.destination_1, form.destination_2, form.destination_3];
          const yr = [form.year_1, form.year_2, form.year_3];

          for (let i = 0; i < albumCount; i++) {
            const im = imports[i];
            if (!im || (!im.coverBytes && !im.interiorBytes)) continue;
            const albumIndex = i + 1;
            const res = await importNativeAlbum({
              shopifyOrderId: order.shopify_order_id,
              albumIndex,
              albumCount,
              destination: dest[i] || null,
              year: yr[i] || null,
              cover: im.coverBytes
                ? {
                    kase: im.coverCase,
                    bytes: im.coverBytes,
                    frontIndex: im.frontIdx,
                    backIndex: im.backIdx,
                    wrapCut: { backRight: im.wrapBackRight, frontLeft: im.wrapFrontLeft },
                  }
                : null,
              interior: im.interiorBytes ? { bytes: im.interiorBytes } : null,
              covers,
              settings,
              onProgress: (m) => setImportProgress(t('Album {n} — {msg}', { n: albumIndex, msg: m })),
            });
            results.push({ albumIndex, ...res });
          }

          setImportDone(true);
          // Report what actually happened. A cover-only import writes an
          // editable cover but CANNOT render print PDFs (the render path needs
          // interior pages), so claiming a flat "terminé" left the operator
          // believing an order was print-ready when nothing had been generated.
          const notRendered = results.filter((r) => !r.rendered);
          setImportProgress(
            notRendered.length === 0
              ? t('Import terminé ✓ — PDF et aperçu 3D générés')
              : t('Import terminé ✓ — album {list} : couverture enregistrée, mais PDF non généré (aucune page intérieure). Ajoutez un intérieur ou régénérez depuis l’éditeur.', {
                  list: notRendered.map((r) => r.albumIndex).join(', '),
                }),
          );
          // Refresh the list but KEEP the modal open on the success state — the
          // admin dismisses via "Fermer" (onCreated would close it immediately).
          onRefresh?.();
        } catch (impErr: any) {
          setError(t('Commande créée mais import du PDF échoué : {error}. Ouvrez la commande pour réessayer.', { error: impErr?.message ?? t('erreur inconnue') }));
        } finally {
          setImportBusy(false);
        }
      } else {
        onCreated();
        navigate(`/admin/orders/${order.shopify_order_id}`);
      }
    } catch (e: any) {
      setError(e?.message ?? t('Erreur lors de la création'));
    } finally {
      setSubmitting(false);
    }
  }

  // Build a WhatsApp wa.me URL with a French message embedding the upload link.
  function whatsappShareUrl(link: { upload_url: string; customer_name: string }, phone: string): string {
    const digits = phone.replace(/\D/g, '');
    const intl = digits.startsWith('212') ? digits :
                 digits.startsWith('0') ? '212' + digits.slice(1) :
                 digits;
    const firstName = link.customer_name.split(' ')[0] || '';
    const text = t('Bonjour {name} 👋\n\nVoici votre lien sécurisé pour envoyer vos photos à Storiz :\n{url}\n\nCliquez, glissez vos photos et c\'est tout. On s\'occupe du reste 📸✨', { name: firstName, url: link.upload_url });
    return `https://wa.me/${intl}?text=${encodeURIComponent(text)}`;
  }

  // Success view for upload-link mode — link is ready, show share controls.
  if (mode === 'upload-link' && generatedLink) {
    const waUrl = whatsappShareUrl(generatedLink, generatedLink.customer_phone);
    return (
      <Modal isOpen={true} onClose={onClose} size="lg" title={t('Lien d\'upload prêt')}>
        <div className="p-6 space-y-5">
          <div className="text-center">
            <div className="text-5xl mb-3">🔗</div>
            <h3 className="text-lg font-semibold text-gray-900">{t('Envoyez ce lien à {name}', { name: generatedLink.customer_name.split(' ')[0] })}</h3>
            <p className="text-sm text-gray-500 mt-1">
              {t('Le client pourra téléverser ses photos sans se connecter. Lien valide jusqu\'au {date}.', { date: new Date(generatedLink.expires_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' }) })}
            </p>
          </div>

          {/* Link box */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-2">
            <input
              readOnly
              value={generatedLink.upload_url}
              onFocus={(e) => e.target.select()}
              className="flex-1 bg-transparent text-sm text-gray-700 font-mono outline-none truncate"
            />
            <Button
              size="sm"
              variant="secondary"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(generatedLink.upload_url);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                } catch { /* clipboard blocked */ }
              }}
            >
              {copied ? t('✓ Copié') : t('Copier')}
            </Button>
          </div>

          {/* WhatsApp share — most clients are on WhatsApp */}
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-3 rounded-lg bg-[#25D366] text-white font-semibold text-sm hover:opacity-90 transition"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            {t('Envoyer sur WhatsApp')}
          </a>

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <Button variant="secondary" onClick={onClose}>{t('Fermer')}</Button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={true}
      // Block backdrop / X close while an import is running so the admin can't
      // lose the progress view (the import keeps running detached otherwise).
      onClose={importBusy ? () => {} : onClose}
      size="lg"
      title={mode === 'upload-link' ? t('Nouveau lien d\'upload client') : t('Nouvelle commande manuelle')}
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-5">
        {mode === 'upload-link' && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-xs text-blue-800">
            {t('Remplissez les infos client. Après création, vous obtiendrez un lien à envoyer au client pour qu\'il téléverse ses photos.')}
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <Section title={t('Client')}>
          <Field label={t('Nom complet *')} required>
            <input
              type="text" value={form.customer_name}
              onChange={(e) => update('customer_name', e.target.value)}
              className={inputCls} placeholder={t('Ex: Sara Bennani')} required
            />
          </Field>
          <Field label={t('WhatsApp *')} required>
            <input
              type="tel" value={form.customer_phone}
              onChange={(e) => update('customer_phone', e.target.value)}
              className={inputCls} placeholder={t('Ex: 0612345678')} required
            />
          </Field>
          <Field label={t('Email')}>
            <input
              type="email" value={form.customer_email}
              onChange={(e) => update('customer_email', e.target.value)}
              className={inputCls} placeholder={t('optionnel')}
            />
          </Field>
        </Section>

        <Section title={t('Commande')}>
          <Field label={t('Formule *')} required>
            <select
              value={form.bundle}
              onChange={(e) => update('bundle', e.target.value as 'Solo' | 'Duo' | 'Trio')}
              className={inputCls}
            >
              <option value="Solo">{t('Solo (1 album)')}</option>
              <option value="Duo">{t('Duo (2 albums)')}</option>
              <option value="Trio">{t('Trio (3 albums)')}</option>
            </select>
          </Field>
          <Field label={t('Nombre de pages *')} required>
            <input
              type="number" min={1} max={300} value={form.page_count}
              onChange={(e) => update('page_count', parseInt(e.target.value, 10) || BASE_PAGES_PER_ALBUM)}
              className={inputCls}
            />
          </Field>
          <Field label={t('Prix total (MAD)')}>
            <input
              type="number" min={0} step="0.01" value={form.total_price}
              onChange={(e) => update('total_price', e.target.value)}
              className={inputCls} placeholder={t('optionnel')}
            />
          </Field>
          <Field label={t('Mise en page')}>
            <input
              type="text" value={form.layout}
              onChange={(e) => update('layout', e.target.value)}
              className={inputCls} placeholder={t('Ex: 1 image par page')}
            />
          </Field>
          <Field label={t('Origine')}>
            <OriginSelect value={form.origin} onChange={(v) => update('origin', v)} />
          </Field>
        </Section>

        <Section title={t('Destinations ({n} album(s))', { n: albumCount })}>
          {Array.from({ length: albumCount }, (_, i) => {
            const idx = i + 1;
            const destKey = `destination_${idx}` as 'destination_1' | 'destination_2' | 'destination_3';
            const yearKey = `year_${idx}` as 'year_1' | 'year_2' | 'year_3';
            return (
              <div key={idx} className="grid grid-cols-2 gap-3 col-span-2">
                <Field label={t('Destination {n}', { n: idx })}>
                  <input
                    type="text" value={form[destKey]}
                    onChange={(e) => update(destKey, e.target.value)}
                    className={inputCls} placeholder={t('Ex: Bali')}
                  />
                </Field>
                <Field label={t('Année {n}', { n: idx })}>
                  <input
                    type="text" value={form[yearKey]}
                    onChange={(e) => update(yearKey, e.target.value)}
                    className={inputCls} placeholder={t('Ex: 2026')}
                  />
                </Field>
              </div>
            );
          })}
        </Section>

        {mode === 'order' && (
          <Section title={t('Importer des PDF (client)')}>
            <div className="col-span-2 space-y-3">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={importEnabled}
                  onChange={(e) => setImportEnabled(e.target.checked)}
                  className="h-4 w-4"
                />
                {t('Importer des PDF déjà finis — couverture et/ou intérieur, par album. Refit dans nos specs + notre branding.')}
              </label>

              {importEnabled && (
                <div className="space-y-4">
                  {importErr && <div className="text-xs text-red-600">{importErr}</div>}

                  {imports.map((im, i) => (
                    <div key={i} className="space-y-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
                      {albumCount > 1 && (
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          {t('Album {n}', { n: i + 1 })}
                        </div>
                      )}

                      {/* ── COVER ── */}
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-3">
                          <Field label={t('Couverture (PDF) — optionnel')}>
                            <input
                              type="file" accept="application/pdf"
                              onChange={(e) => onCoverFileSelect(i, e.target.files?.[0] ?? null)}
                              className="text-sm"
                            />
                            <SpecVerdict check={im.coverCheck} />
                          </Field>
                          {im.coverBytes && (
                            <Field label={t('Type de couverture')}>
                              <select
                                value={im.coverCase}
                                onChange={(e) => {
                                  const kase = e.target.value as CoverCase;
                                  patchImport(i, { coverCase: kase });
                                  refreshCoverThumbs(i, { ...im, coverCase: kase });
                                }}
                                className={inputCls}
                              >
                                <option value="A">{t('A — Wrap complet (recto+dos en 1 page)')}</option>
                                <option value="B">{t('B — Recto + dos séparés')}</option>
                                <option value="C">{t('C — Recto seul (A4)')}</option>
                              </select>
                            </Field>
                          )}
                        </div>

                        {im.coverBytes && (
                          <>
                            <div className="text-[11px] text-gray-500">
                              {t('{file} — {n} page(s)', { file: im.coverFileName, n: im.coverPages })}
                            </div>

                            {im.coverCase === 'A' && (
                              <div className="grid grid-cols-2 gap-3">
                                <Field label={t('Fin du dos (% largeur)')}>
                                  <input
                                    type="number" min={5} max={95}
                                    value={Math.round(im.wrapBackRight * 100)}
                                    onChange={(e) => patchImport(i, { wrapBackRight: Math.max(0.05, Math.min(0.95, (parseInt(e.target.value, 10) || 48) / 100)) })}
                                    className={inputCls}
                                  />
                                </Field>
                                <Field label={t('Début du recto (% largeur)')}>
                                  <input
                                    type="number" min={5} max={95}
                                    value={Math.round(im.wrapFrontLeft * 100)}
                                    onChange={(e) => patchImport(i, { wrapFrontLeft: Math.max(0.05, Math.min(0.95, (parseInt(e.target.value, 10) || 52) / 100)) })}
                                    className={inputCls}
                                  />
                                </Field>
                              </div>
                            )}

                            {(im.coverCase === 'B' || im.coverCase === 'C') && (
                              <div className="grid grid-cols-2 gap-3">
                                <Field label={t('Page recto')}>
                                  <input
                                    type="number" min={1} max={Math.max(1, im.coverPages)}
                                    value={im.frontIdx + 1}
                                    onChange={(e) => {
                                      const v = Math.max(0, Math.min(im.coverPages - 1, (parseInt(e.target.value, 10) || 1) - 1));
                                      patchImport(i, { frontIdx: v });
                                      refreshCoverThumbs(i, { ...im, frontIdx: v });
                                    }}
                                    className={inputCls}
                                  />
                                </Field>
                                {im.coverCase === 'B' && (
                                  <Field label={t('Page dos — 0 = aucun')}>
                                    <input
                                      type="number" min={0} max={Math.max(0, im.coverPages)}
                                      value={im.backIdx + 1}
                                      onChange={(e) => {
                                        const raw = parseInt(e.target.value, 10);
                                        const v = !raw || raw < 1 ? -1 : Math.min(im.coverPages - 1, raw - 1);
                                        patchImport(i, { backIdx: v });
                                        refreshCoverThumbs(i, { ...im, backIdx: v });
                                      }}
                                      className={inputCls}
                                    />
                                  </Field>
                                )}
                              </div>
                            )}

                            <div className="flex gap-3 items-end">
                              <div className="text-center">
                                <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">
                                  {im.coverCase === 'A' ? t('Wrap (recto à droite, dos à gauche)') : t('Recto')}
                                </div>
                                {im.thumbs.front
                                  ? <img src={im.thumbs.front} alt="" className="h-24 w-auto border border-gray-300 rounded shadow-sm" />
                                  : <div className="h-24 w-16 bg-gray-200 rounded animate-pulse" />}
                              </div>
                              {im.coverCase === 'B' && im.backIdx >= 0 && (
                                <div className="text-center">
                                  <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">{t('Dos')}</div>
                                  {im.thumbs.back
                                    ? <img src={im.thumbs.back} alt="" className="h-24 w-auto border border-gray-300 rounded shadow-sm" />
                                    : <div className="h-24 w-16 bg-gray-200 rounded animate-pulse" />}
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>

                      {/* ── INTERIOR ── */}
                      <div className="border-t border-gray-200 pt-2 space-y-1">
                        <Field label={t('Intérieur (PDF A4) — optionnel')}>
                          <input
                            type="file" accept="application/pdf"
                            onChange={(e) => onInteriorFileSelect(i, e.target.files?.[0] ?? null)}
                            className="text-sm"
                          />
                          <SpecVerdict check={im.interiorCheck} />
                        </Field>
                        {im.interiorBytes && (
                          <div className="text-xs text-gray-700 bg-white border border-gray-200 rounded px-2 py-1">
                            {t('{n} pages intérieures — nos pages intro/outro seront ajoutées', { n: im.interiorPages })}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {importProgress && (
                    <div className={`text-sm ${importDone ? 'text-green-700' : 'text-blue-700'}`}>
                      {importProgress}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Section>
        )}

        <Section title={t('Note')}>
          <div className="col-span-2">
            <Field label={t('Note interne')}>
              <textarea
                value={form.customer_note}
                onChange={(e) => update('customer_note', e.target.value)}
                rows={2}
                className={inputCls}
                placeholder={t('Ex: paiement en espèces, livraison spéciale...')}
              />
            </Field>
          </div>
        </Section>

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting || importBusy}>
            {importDone ? t('Fermer') : t('Annuler')}
          </Button>
          {importDone ? (
            <Button
              type="button"
              onClick={() => {
                if (createdOrderId) navigate(`/admin/orders/${createdOrderId}`);
                else onClose();
              }}
            >
              {t('Voir la commande →')}
            </Button>
          ) : (
            <Button type="submit" loading={submitting || importBusy} disabled={submitting || importBusy || (importEnabled && (!imports.some((im) => im.coverBytes || im.interiorBytes) || importBlocked))}>
              {mode === 'upload-link'
                ? t('Créer + générer le lien')
                : importEnabled
                  ? t('Créer + importer le PDF')
                  : t('Créer la commande')}
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
}

const inputCls = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{title}</p>
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function Field({
  label, required, children,
}: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-gray-600 mb-1 block">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}

// ─── Status pill + inline changer ────────────────────────────────────────────
// Extracted from OrderRow so the mobile card and the desktop table drive status
// through the SAME code: one dropdown, one confirmation dialog, one update call.
// The menu is portalled to <body> with fixed positioning so it escapes the
// table's overflow-x-auto and the card list's overflow-hidden.
function OrderStatusControl({
  order, onStatusChanged,
}: { order: Order; onStatusChanged: (s: string) => void }) {
  const { t } = useT();
  const [menuOpen, setMenuOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);

  const isAdminRole = useAuthStore((st) => st.profile?.role === 'admin');
  const statusOptions = STATUSES.filter((so) => so.value);
  const pendingLabel = STATUSES.find((so) => so.value === pendingStatus)?.label ?? pendingStatus;

  const confirmChange = async () => {
    if (!pendingStatus) return;
    setSaving(true);
    try {
      // A photo placed twice asks « Continuer quand même ? »; no advance lets
      // an admin force it with a reason (lib/statusChangeAsk.ts).
      await changeStatusAsking(order.shopify_order_id, pendingStatus, isAdminRole);
      onStatusChanged(pendingStatus);
      setPendingStatus(null);
    } catch (err) {
      console.error('[orders] status update failed', err);
      // The worker says WHY (409 with the gate's reason) — show it, or « try
      // again » is the only advice and trying again can never work (#WA-459570).
      const why = (err as Error)?.message;
      alert(why ? `${t('Statut non modifié :')} ${why}` : t('La mise à jour du statut a échoué. Réessayez.'));
    } finally {
      setSaving(false);
    }
  };

  const place = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const MENU_W = 176, MENU_H = 288;
    const left = Math.min(r.left, window.innerWidth - MENU_W - 8);
    const openUp = r.bottom + MENU_H > window.innerHeight && r.top > MENU_H;
    setMenuPos({ top: openUp ? r.top - MENU_H - 4 : r.bottom + 4, left: Math.max(8, left) });
  };

  useEffect(() => {
    if (!menuOpen) return;
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [menuOpen]);


  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => { if (menuOpen) { setMenuOpen(false); } else { place(); setMenuOpen(true); } }}
        className="inline-flex items-center gap-1 min-h-11 md:min-h-0 -my-2.5 md:my-0 rounded-full hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-brand-400/40"
        title={t('Changer le statut')}
        aria-haspopup="listbox"
        aria-expanded={menuOpen}
      >
        <OrderStatusBadge order={order} />
        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {menuOpen && menuPos && createPortal(
        <>
          <div className="fixed inset-0 z-[95]" onClick={() => setMenuOpen(false)} />
          <div
            role="listbox"
            className="fixed z-[96] w-44 rounded-lg border border-gray-200 bg-white shadow-lg py-1 max-h-72 overflow-auto"
            style={{ top: menuPos.top, left: menuPos.left }}
            onClick={(e) => e.stopPropagation()}
          >
            {statusOptions.map((so) => {
              const isCurrent = so.value === order.status;
              return (
                <button
                  key={so.value}
                  type="button"
                  onClick={() => { setMenuOpen(false); if (!isCurrent) setPendingStatus(so.value); }}
                  className={`w-full text-left px-3 py-3 md:py-2 text-sm flex items-center justify-between transition-colors ${isCurrent ? 'text-gray-400 bg-gray-50' : 'text-gray-700 hover:bg-brand-50'}`}
                >
                  {t(so.label)}
                  {isCurrent && <span className="text-[10px] uppercase tracking-wide">{t('actuel')}</span>}
                </button>
              );
            })}
          </div>
        </>,
        document.body,
      )}

      {pendingStatus && (
        <Modal isOpen onClose={() => { if (!saving) setPendingStatus(null); }} title={t('Changer le statut')}>
          <div className="space-y-5">
            <p className="text-sm text-gray-600 leading-relaxed">
              {t('Changer le statut de')} <span className="font-semibold text-gray-900">{order.shopify_order_name}</span> {t('en')}{' '}
              <span className="font-semibold text-gray-900">«&nbsp;{t(pendingLabel ?? '')}&nbsp;»</span> ?
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setPendingStatus(null)} disabled={saving}>{t('Annuler')}</Button>
              <Button onClick={confirmChange} disabled={saving}>{saving ? t('Mise à jour…') : t('Confirmer')}</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Mobile order card ───────────────────────────────────────────────────────
// Everything the desktop table shows across eight columns, in four lines:
//   1  select · needs-action dot · source/device · number ......... status
//   2  customer name · phone
//   3  what was bought (thumbnails, formule, add-on) ......... price
//   4  destination · date
// Price and formule are the fields that were invisible on a phone, so they get
// real weight here; destination and date stay quiet at the bottom.
/**
 * « Qui a envoyé l'argent, qui non » — the manager's question the status
 * column cannot answer, because a deposit does not change the status. Green
 * when nothing is left to pay, gold with the amount when an advance is
 * recorded, blue when her slip waits for a person. No chip = nothing received.
 *
 * ⚠️ 2026-10-06: the one definition (lib/paidState.ts). This read
 * paid_in_full | advance_received_amount and showed NOTHING for the 45 orders
 * whose advance is the AMEEX card's acompte (#WA-168784's 100 MAD).
 */
function PaymentChip({ order }: { order: Order }) {
  const { t } = useT();
  const s = paidState(order);
  if (s.level === 'full') {
    return (
      <span className="text-[10px] bg-emerald-100 text-emerald-900 px-1.5 py-0.5 rounded-full font-bold shrink-0"
            title={t('Rien à payer : payé en entier, ou livré')}>
        💰 {t('Payé')}
      </span>
    );
  }
  if (s.level === 'advance') {
    return (
      <span className="text-[10px] bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded-full font-bold shrink-0 tabular-nums"
            title={t('Avance reçue — le reste à la livraison')}>
        💰 {s.received > 0 ? Math.round(s.received) : '✓'}
      </span>
    );
  }
  if (s.level === 'receipt_pending') {
    return (
      <span className="text-[10px] bg-sky-100 text-sky-900 px-1.5 py-0.5 rounded-full font-bold shrink-0"
            title={t('Reçu envoyé — à vérifier par une personne')}>
        🧾 {t('Reçu à vérifier')}
      </span>
    );
  }
  return null;
}

function OrderCard({
  order, selected, onToggle, onStatusChanged,
}: { order: Order; selected: boolean; onToggle: () => void; onStatusChanged: (s: string) => void }) {
  const { t } = useT();
  const navigate = useNavigate();
  const destinations = [order.destination_1, order.destination_2, order.destination_3]
    .filter(Boolean).join(', ');
  const act = orderActionState({ order });
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <li
      onClick={() => navigate(`/admin/orders/${order.shopify_order_id}`)}
      className={`px-3 py-3 cursor-pointer transition-colors ${selected ? 'bg-red-50/40 dark:bg-red-500/[0.07]' : 'active:bg-gray-50'}`}
    >
      <div className="flex items-start gap-1">
        <SelectTick
          className="-ml-2.5 -mt-2.5"
          checked={selected}
          onChange={onToggle}
          label={t('Sélectionner {name}', { name: order.shopify_order_name })}
        />

        <div className="min-w-0 flex-1">
          {/* 1 — identity + state. Wraps rather than truncating the order
              number when a long status (« En production (fabrication) »)
              does not fit beside it on a 375px phone. */}
          <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-0.5">
            <span className="inline-flex items-center gap-1.5 min-h-6 shrink-0">
              {act && (
                <span className="w-2 h-2 rounded-full shrink-0 dark:[--adm-dot-blue:#93C5FD]" style={{ background: act.color }}
                      title={act.label} aria-label={act.label} role="img" />
              )}
              <SourceBadge source={order.traffic_source} />
              <DeviceBadge device={order.device} size={16} />
              <span className="font-semibold text-gray-900 whitespace-nowrap">{order.shopify_order_name}</span>
            </span>
            <span onClick={stop} className="shrink-0 ml-auto">
              <OrderStatusControl order={order} onStatusChanged={onStatusChanged} />
            </span>
          </div>

          {/* 2 — who */}
          <p className="mt-1 text-[13px] font-medium text-gray-800 truncate">{order.customer_name ?? '—'}</p>
          <p className="text-xs text-gray-400">{formatPhone(order.customer_phone)}</p>

          {/* 3 — what was bought, and for how much */}
          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1.5 min-w-0">
              <OrderItemThumbs items={order.items} />
              {order.bundle && (
                <span className="text-[11px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full font-medium shrink-0">
                  {order.bundle} · {order.page_count}p
                </span>
              )}
              {order.relief_3d && (
                <span className="text-[10px] bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded-full font-bold shrink-0"
                      title={t('Couverture Relief 3D')}>
                  ✦ 3D
                </span>
              )}
            </span>
            <span className="inline-flex items-center gap-1.5 shrink-0">
              <PaymentChip order={order} />
              <span className="text-sm font-semibold text-gray-900 tabular-nums">
                {formatPrice(order.total_price)}
              </span>
            </span>
          </div>

          {/* 4 — context, kept quiet */}
          {(destinations || order.created_at) && (
            <p className="mt-1.5 text-[11px] text-gray-400 truncate">
              {destinations && <span>{destinations}</span>}
              {destinations && order.created_at && <span className="mx-1.5">·</span>}
              {order.created_at && <span>{formatDateTime(order.created_at)}</span>}
            </p>
          )}
        </div>
      </div>
    </li>
  );
}

function OrderRow({
  order, selected, onToggle, onStatusChanged,
}: { order: Order; selected: boolean; onToggle: () => void; onStatusChanged: (newStatus: string) => void }) {
  const { t } = useT();
  const navigate = useNavigate();
  const destinations = [order.destination_1, order.destination_2, order.destination_3]
    .filter(Boolean)
    .join(', ');


  // Fix 1 — the whole row opens the order. Interactive cells (checkbox, status
  // pill, "Voir") stopPropagation so they don't also trigger navigation.
  const open = () => navigate(`/admin/orders/${order.shopify_order_id}`);
  const stop = (e: React.MouseEvent) => e.stopPropagation();


  return (
    <tr
      className={`transition-colors cursor-pointer ${selected ? 'bg-red-50/40 dark:bg-red-500/[0.07]' : 'hover:bg-gray-50/50'}`}
      onClick={open}
      onKeyDown={(e) => { if (e.key === 'Enter') open(); }}
      tabIndex={0}
      role="button"
      aria-label={t('Ouvrir la commande {name}', { name: order.shopify_order_name })}
    >
      <td className="px-3 py-3.5 w-10" onClick={stop}>
        <input
          type="checkbox"
          aria-label={t('Sélectionner {name}', { name: order.shopify_order_name })}
          checked={selected}
          onChange={onToggle}
          className="rounded border-gray-300"
        />
      </td>
      <td className="px-4 py-3.5">
        <span className="inline-flex items-center gap-1.5">
          {/* Action indicator: one quiet dot, only when this order needs
              something. No dot = nothing to do (see lib/orderActionState.ts).
              The list has no follow-up sub-state to hand in, so rows surface the
              order-level needs; the Relances page owns the follow-up view. */}
          {(() => {
            const act = orderActionState({ order });
            if (!act) return null;
            return (
              <span
                className="w-2 h-2 rounded-full shrink-0 dark:[--adm-dot-blue:#93C5FD]"
                style={{ background: act.color }}
                title={act.label}
                aria-label={act.label}
                role="img"
              />
            );
          })()}
          <SourceBadge source={order.traffic_source} />
          <DeviceBadge device={order.device} size={18} />
          <span className="font-medium text-gray-900">{order.shopify_order_name}</span>
        </span>
      </td>
      <td className="px-4 py-3.5">
        <div>
          <p className="font-medium text-gray-800">{order.customer_name ?? '—'}</p>
          <p className="text-xs text-gray-400">{formatPhone(order.customer_phone)}</p>
        </div>
      </td>
      <td className="px-4 py-3.5 hidden md:table-cell">
        {order.bundle ? (
          <span className="inline-flex items-center gap-1">
            {/* What's actually in the order. Lives in this column because it is
                already hidden below md, so a row can gain pictures without any
                risk to the mobile layout. */}
            <OrderItemThumbs items={order.items} />
            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full font-medium">
              {order.bundle} · {order.page_count}p
            </span>
            {order.relief_3d && (
              <span
                className="text-[10px] bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded-full font-bold"
                title={t('Couverture Relief 3D')}
              >
                ✦ 3D
              </span>
            )}
          </span>
        ) : '—'}
      </td>
      <td className="px-4 py-3.5 hidden lg:table-cell">
        <span className="text-gray-600 text-xs">{destinations || '—'}</span>
      </td>
      <td className="px-4 py-3.5 hidden lg:table-cell">
        <span className="inline-flex items-center gap-1.5">
          <PaymentChip order={order} />
          <span className="font-medium text-gray-700">{formatPrice(order.total_price)}</span>
        </span>
      </td>
      <td className="px-4 py-3.5" onClick={stop}>
        <OrderStatusControl order={order} onStatusChanged={onStatusChanged} />
      </td>
      <td className="px-4 py-3.5 hidden md:table-cell">
        <span className="text-xs text-gray-400 whitespace-nowrap">{formatDateTime(order.created_at)}</span>
      </td>
      <td className="px-4 py-3.5" onClick={stop}>
        <Link to={`/admin/orders/${order.shopify_order_id}`}>
          <Button variant="ghost" size="sm">{t('Voir →')}</Button>
        </Link>
      </td>
    </tr>
  );
}

// ─── Bulk Status Modal ───────────────────────────────────────────────────────
// Apply ONE status to every selected order. Reuses the per-order
// ordersApi.updateStatus so all side-effects stay correct: setting "Payée"
// fires the Purchase event once per order, the no-print-before-paid gate still
// applies, and AMEEX transitions are untouched. Any order the server refuses
// surfaces as "ignorée" with the reason — it never blocks the others. Status
// changes are reversible, so no typed confirmation (unlike bulk delete).
function BulkStatusModal({
  orders, onClose, onDone,
}: { orders: Order[]; onClose: () => void; onDone: () => void }) {
  const { t } = useT();
  const [target, setTarget] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [results, setResults] = useState<
    Array<{ id: string; ok: boolean; name?: string; error?: string }> | null
  >(null);

  const options = STATUSES.filter(s => s.value); // drop the "Tous les statuts" filter entry
  const targetLabel = STATUSES.find(s => s.value === target)?.label ?? target;
  // 5 PATCHes in parallel per round so a 50-order change is responsive without
  // hammering the worker; each call is the same single-order transition.
  const CHUNK = 5;

  async function apply() {
    if (!target) return;
    setSubmitting(true);
    setProgress({ done: 0, total: orders.length });
    const collected: Array<{ id: string; ok: boolean; name?: string; error?: string }> = [];
    for (let i = 0; i < orders.length; i += CHUNK) {
      const chunk = orders.slice(i, i + CHUNK);
      const settled = await Promise.allSettled(
        chunk.map(o => ordersApi.updateStatus(o.shopify_order_id, target)),
      );
      settled.forEach((r, j) => {
        const o = chunk[j];
        collected.push(r.status === 'fulfilled'
          ? { id: o.shopify_order_id, ok: true, name: o.shopify_order_name ?? undefined }
          : { id: o.shopify_order_id, ok: false, name: o.shopify_order_name ?? undefined, error: (r.reason as Error)?.message ?? t('Échec') });
      });
      setProgress({ done: collected.length, total: orders.length });
    }
    setResults(collected);
    setSubmitting(false);
    if (collected.every(r => r.ok)) setTimeout(onDone, 600);
  }

  return (
    <Modal isOpen={true} onClose={results ? onDone : onClose} size="lg" title={t('Changer le statut des commandes sélectionnées')}>
      <div className="p-6 space-y-4">
        {!results ? (
          <>
            <div className="text-sm text-gray-700">
              {t('{n} commande(s) passeront au statut choisi :', { n: orders.length })}
            </div>
            <select
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              disabled={submitting}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-500"
              autoFocus
            >
              <option value="">{t('— Choisir un statut —')}</option>
              {options.map(s => <option key={s.value} value={s.value}>{t(s.label)}</option>)}
            </select>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
              {t('Le passage à « Payée » déclenche le suivi d’achat (une fois par commande). Toute commande qui ne peut pas adopter ce statut sera signalée comme « ignorée » ci-dessous.')}
            </div>

            <div className="max-h-52 overflow-y-auto rounded-lg border border-gray-100 divide-y divide-gray-50">
              {orders.map(o => (
                <div key={o.id} className="flex items-center justify-between px-3 py-2 text-xs gap-2">
                  <span className="font-medium text-gray-800 min-w-0 truncate">
                    {o.shopify_order_name}<span className="text-gray-500"> · {o.customer_name ?? '—'}</span>
                  </span>
                  <span className="shrink-0"><OrderStatusBadge order={o} /></span>
                </div>
              ))}
            </div>

            {submitting && progress && (
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs text-gray-600">
                  <span>{t('Mise à jour en cours…')}</span>
                  <span className="font-mono">{progress.done} / {progress.total}</span>
                </div>
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-brand-500 transition-all duration-300 ease-out"
                    style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }} />
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button size="sm" variant="ghost" onClick={onClose} disabled={submitting}>{t('Annuler')}</Button>
              <Button size="sm" variant="primary" onClick={apply} disabled={!target || submitting}>
                {submitting ? t('Application…') : t('Appliquer « {s} »', { s: targetLabel })}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="text-sm text-gray-900">
              {t('{ok} mise(s) à jour, {fail} ignorée(s)', { ok: results.filter(r => r.ok).length, fail: results.filter(r => !r.ok).length })}
            </div>
            <div className="max-h-60 overflow-y-auto rounded-lg border border-gray-100 divide-y divide-gray-50">
              {results.map(r => (
                <div key={r.id} className="flex items-center justify-between px-3 py-2 text-xs gap-2">
                  <span className="font-medium text-gray-800 min-w-0 truncate">{r.name ?? r.id}</span>
                  {r.ok
                    ? <span className="text-green-600 shrink-0">✓ {targetLabel}</span>
                    : <span className="text-red-600 shrink-0 truncate max-w-[55%]" title={r.error}>✕ {r.error}</span>}
                </div>
              ))}
            </div>
            <div className="flex justify-end pt-2">
              <Button size="sm" variant="primary" onClick={onDone}>{t('Fermer')}</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ─── Bulk Delete Modal ──────────────────────────────────────────────────────
// Typed confirmation: admin must type SUPPRIMER (matches the language of the
// surrounding UI) to enable the red button. We list every selected order so
// nothing is hidden behind a count. Per-order results are surfaced after
// the call so failed deletes are visible (e.g. Shopify rejected the cancel).
function BulkDeleteModal({
  orders, onClose, onDone,
}: { orders: Order[]; onClose: () => void; onDone: () => void }) {
  const { t } = useT();
  const [confirmation, setConfirmation] = useState('');
  const [syncShopify, setSyncShopify] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Running progress: how many we've finished + per-order outcomes so far.
  // We chunk requests to the worker so a 50-order delete is 10 small calls
  // instead of one mega-call that the browser fetch would time out on.
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [results, setResults] = useState<
    Array<{ id: string; ok: boolean; shopify_order_name?: string; error?: string }>
    | null
  >(null);

  const ids = orders.map(o => o.shopify_order_id);
  const expected = 'SUPPRIMER';
  // Each delete makes ~6 Worker subrequests (Supabase join select, AMEEX,
  // Shopify, two storage removes, row delete). The Worker has a hard
  // per-invocation subrequest cap of 50 on the bundled plan, so we run
  // 5 orders in parallel per worker call to stay under it with margin.
  // The progress bar makes the chunking visible so a 50-order batch
  // doesn't feel slow.
  const CHUNK_SIZE = 5;

  async function handleDelete() {
    setSubmitting(true);
    setProgress({ done: 0, total: ids.length });
    const collected: Array<{ id: string; ok: boolean; shopify_order_name?: string; error?: string }> = [];
    try {
      for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
        const chunk = ids.slice(i, i + CHUNK_SIZE);
        try {
          const res = await ordersApi.bulkDelete(chunk, { sync_shopify: syncShopify, sync_ameex: true });
          collected.push(...res.results);
        } catch (chunkErr: any) {
          // Whole chunk failed (e.g. network). Mark each id as failed so
          // the user can see exactly which ones to retry.
          for (const id of chunk) {
            collected.push({ id, ok: false, error: chunkErr?.message ?? t('Erreur réseau') });
          }
        }
        setProgress({ done: collected.length, total: ids.length });
      }
      setResults(collected);
      // Close automatically only if everything succeeded — otherwise we
      // leave the modal up so the admin can read which rows failed.
      if (collected.every(r => r.ok)) {
        setTimeout(onDone, 600);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal isOpen={true} onClose={results ? onDone : onClose} size="lg" title={t('Supprimer les commandes sélectionnées')}>
      <div className="p-6 space-y-4">
        {!results ? (
          <>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-900">
              <strong>{t('Action irréversible.')}</strong> {t('Toutes les photos, PDF et colis AMEEX associés seront supprimés.')}
              {syncShopify && ' ' + t('Les brouillons Shopify seront supprimés et les commandes Shopify réelles seront annulées.')}
            </div>

            <div className="max-h-60 overflow-y-auto rounded-lg border border-gray-100 divide-y divide-gray-50">
              {orders.map(o => (
                <div key={o.id} className="flex items-center justify-between px-3 py-2 text-xs">
                  <div className="min-w-0">
                    <span className="font-medium text-gray-800">{o.shopify_order_name}</span>
                    <span className="text-gray-500"> · {o.customer_name ?? '—'} · {o.bundle ?? '—'}</span>
                  </div>
                  <span className="text-gray-500 font-medium ml-2 shrink-0">{formatPrice(o.total_price)}</span>
                </div>
              ))}
            </div>

            <label className="flex items-center gap-2 text-xs text-gray-700">
              <input
                type="checkbox"
                checked={syncShopify}
                onChange={(e) => setSyncShopify(e.target.checked)}
                className="rounded"
              />
              {t('Synchroniser avec Shopify (recommandé)')}
            </label>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('Tapez')} <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">{expected}</span> {t('pour confirmer')}
              </label>
              <input
                type="text"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                placeholder={expected}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-400"
                autoFocus
              />
            </div>

            {submitting && progress && (
              <div className="space-y-1.5">
                <div className="flex justify-between text-xs text-gray-600">
                  <span>{t('Suppression en cours…')}</span>
                  <span className="font-mono">{progress.done} / {progress.total}</span>
                </div>
                <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-red-500 transition-all duration-300 ease-out"
                    style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }}
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-1 border-t border-gray-100">
              <Button variant="secondary" onClick={onClose} disabled={submitting}>{t('Annuler')}</Button>
              <Button
                variant="danger"
                loading={submitting}
                disabled={confirmation.trim().toUpperCase() !== expected}
                onClick={handleDelete}
              >
                {t('Supprimer {n} commande(s)', { n: orders.length })}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className={`rounded-lg p-3 text-sm ${results.every(r => r.ok) ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-900'}`}>
              {t('{ok} sur {total} supprimée(s).', { ok: results.filter(r => r.ok).length, total: results.length })}
              {results.some(r => !r.ok) && ' ' + t('Voir les détails ci-dessous.')}
            </div>
            <div className="max-h-60 overflow-y-auto rounded-lg border border-gray-100 divide-y divide-gray-50">
              {results.map(r => (
                <div key={r.id} className="flex items-center justify-between px-3 py-2 text-xs">
                  <span className="font-mono text-gray-700">{r.shopify_order_name ?? r.id}</span>
                  <span className={r.ok ? 'text-green-700' : 'text-red-700'}>
                    {r.ok ? t('✓ Supprimée') : `✗ ${r.error ?? t('Échec')}`}
                  </span>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-1 border-t border-gray-100">
              <Button onClick={onDone}>{t('Fermer')}</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// ─── Send-to-Printer Modal ───────────────────────────────────────────────────
// Confirms the batch (#orders / #files), then collects + zips every selected
// order's PDFs CLIENT-side (sendToPrinter), uploads the zip, and — per the
// independent admin toggles — emails the printer (MailChannels via the worker)
// and/or opens a pre-filled wa.me link. A direct download link is ALWAYS shown.
function SendToPrinterModal({
  orders, onClose,
}: { orders: Order[]; onClose: () => void }) {
  const { t } = useT();
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<SendToPrinterResult | null>(null);
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Estimated file count: 2 PDFs (cover + interior) per album.
  const fileEstimate = orders.reduce((sum, o) => sum + albumCountForBundle(o.bundle) * 2, 0);

  async function handleSend() {
    setSubmitting(true);
    setError(null);
    try {
      // Instant: just pick the print-ready orders + mint the token. No zipping or
      // uploading here — the printer page builds each order's ZIP on demand.
      const res = selectReadyOrders(orders);
      if (res.shopifyOrderIds.length === 0) {
        setError(t('Aucune commande prête : régénérez les PDF puis réessayez.'));
        return;
      }
      // Await the server FIRST, then flip to the result view only on success.
      // FIX (2026-07-06): previously setResult() ran BEFORE this await, so when
      // the server rejected the batch (e.g. every order skipped for missing
      // print originals → 409) the modal was already showing "Création du lien…"
      // and the error was hidden → it hung forever. Now a rejection stays on
      // this view and shows exactly which orders were dropped and why.
      const { token } = await ordersApi.createPrinterLink(res.shopifyOrderIds);
      setResult(res);
      setLinkUrl(`${window.location.origin}/imprimeur/${token}`);
    } catch (e: any) {
      const body = e?.body as
        | { skipped_missing_originals?: Array<{ shopify_order_id: string; missing: number }> }
        | undefined;
      const skipped = body?.skipped_missing_originals ?? [];
      if (skipped.length > 0) {
        const nameFor = (sid: string) =>
          orders.find((o) => String(o.shopify_order_id) === String(sid))?.shopify_order_name ?? sid;
        const list = skipped.map((s) => `${nameFor(s.shopify_order_id)} — ${t('{n} photo(s) manquante(s)', { n: s.missing })}`).join(', ');
        setError(t('Impossible d\'envoyer : originaux manquants ({list}). Le client doit re-téléverser ces photos avant l\'impression.', { list }));
      } else {
        setError(e?.message ?? t('Échec de la création du lien.'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function copyLink() {
    if (!linkUrl) return;
    try {
      await navigator.clipboard.writeText(linkUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked — the field is selectable as a fallback */ }
  }

  return (
    <Modal isOpen={true} onClose={result ? onClose : (submitting ? () => {} : onClose)} size="lg" title={t('Lien imprimeur (commande groupée)')}>
      <div className="p-6 space-y-4">
        {!result ? (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
              {t('{orders} commande(s) · ~{files} fichier(s) PDF.', { orders: orders.length, files: fileEstimate })}{' '}
              {t('Un ZIP par commande est préparé dans votre navigateur, puis réuni dans UN lien (valable 7 jours, sans connexion). Les commandes dont le PDF n\'est pas prêt sont ignorées — régénérez-les d\'abord.')}
            </div>

            <div className="max-h-60 overflow-y-auto rounded-lg border border-gray-100 divide-y divide-gray-50">
              {orders.map(o => (
                <div key={o.id} className="flex items-center justify-between px-3 py-2 text-xs">
                  <div className="min-w-0">
                    <span className="font-medium text-gray-800">{o.shopify_order_name}</span>
                    <span className="text-gray-500"> · {o.bundle ?? '—'} {t('({n} album(s))', { n: albumCountForBundle(o.bundle) })}</span>
                  </div>
                  <span className={`font-medium ml-2 shrink-0 ${o.pdf_status === 'ready' ? 'text-green-600' : 'text-amber-600'}`}>
                    {o.pdf_status === 'ready' ? t('PDF prêt') : t('à régénérer')}
                  </span>
                </div>
              ))}
            </div>

            {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">{error}</div>}

            <div className="flex justify-end gap-2 pt-1 border-t border-gray-100">
              <Button variant="secondary" onClick={onClose} disabled={submitting}>{t('Annuler')}</Button>
              <Button variant="primary" loading={submitting} onClick={handleSend}>
                {t('Générer le lien')}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
              {t('Lien prêt : {n} commande(s) incluse(s).', { n: result.orderIds.length })}
            </div>

            {result.skipped.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
                {t('Ignorée(s) : {list}', { list: result.skipped.map(s => `${s.orderName} (${s.reason})`).join(', ') })}
              </div>
            )}

            {linkUrl ? (
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-600">{t('Lien à envoyer à l\'imprimeur (WhatsApp / email) — valable 7 jours, sans connexion :')}</label>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={linkUrl}
                    onFocus={(e) => e.currentTarget.select()}
                    className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-xs font-mono text-gray-800 bg-gray-50"
                  />
                  <Button variant="primary" onClick={copyLink}>{copied ? t('Copié ✓') : t('Copier')}</Button>
                </div>
                <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-brand-600 hover:underline">
                  {t('Ouvrir la page de téléchargement →')}
                </a>
              </div>
            ) : (
              <div className="text-xs text-amber-600">{t('Création du lien…')}</div>
            )}

            <div className="flex justify-end gap-2 pt-1 border-t border-gray-100">
              <Button onClick={onClose}>{t('Fermer')}</Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
